import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService, ApiResponse } from '../api.service';
import { OrgUser, OrgUserResponse, UserMutationResult, UserRole } from '../../interfaces/org-user';
import { InviteUserPayload } from '../../interfaces/invite-user';
import { unwrapApiEnvelope } from '../../shared/data/api-envelope';
import { UserId, asId, idToNumber } from '../../shared/interfaces/ids';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private apiService: ApiService) {}

  /**
   * Every user in the current organization, unwrapped and camelCased.
   *
   * Returns them all: the Manage User and Pending Approvals tabs read this same
   * list and filter it differently, so the filtering belongs to the pages.
   */
  getUsers(): Observable<OrgUser[]> {
    return this.apiService.get<ApiResponse<OrgUserResponse[]>>(`/v1/users`).pipe(
      unwrapApiEnvelope({ array: true, fallbackMessage: $localize`Couldn’t load the user list.` }),
      map((data) => data.map(toOrgUser))
    );
  }

  /**
   * Archives the user, or removes just this organization's membership — the handler
   * decides which from the user's memberships (`decide_delete_action`).
   *
   * Its status maps are keyed by free-text message and code strings with a
   * `200 if success else 500` fallback, so this route is *written* to permit a 200 with
   * `success: false`. It does not emit one today only because the archive model returns
   * `success: True` on its no-op path. See FINDINGS §2.2.
   */
  deleteUser(userId: UserId): Observable<UserMutationResult> {
    return this.apiService.delete<UserMutationResult>(`/v1/users/${idToNumber(userId)}`);
  }

  denyUser(userId: UserId): Observable<UserMutationResult> {
    return this.apiService.post<UserMutationResult>(`/v1/users/deny/${idToNumber(userId)}`, {});
  }

  approveUser(userId: UserId): Observable<UserMutationResult> {
    return this.apiService.post<UserMutationResult>(`/v1/users/approve/${idToNumber(userId)}`, {});
  }

  /**
   * Takes the two fields the endpoint actually uses rather than a whole row, so
   * the service does not depend on either tab's view type.
   */
  editUserRole(userId: UserId, role: UserRole): Observable<UserMutationResult> {
    return this.apiService.patch<UserMutationResult>(`/v1/users/${idToNumber(userId)}/role`, { Role: role });
  }

  /**
   * Invites a user, and reports which of the two success outcomes happened.
   *
   * The handler answers 200 + `success: true` whether or not the invitation email was
   * sent, distinguishing them ONLY in the message
   * (`organization_user_routes.py:664`) — "User invited to join" versus "User added, but
   * the invitation email could not be sent". Callers must show `message`, not a
   * hardcoded success string, or an admin whose email bounced is told it was sent.
   */
  inviteUser(userData: InviteUserPayload): Observable<UserMutationResult> {
    return this.apiService.post<UserMutationResult>(`/v1/organizations/invite`, userData);
  }
}

function toOrgUser(user: OrgUserResponse): OrgUser {
  return {
    userId: asId<'User'>(user.UserID),
    name: user.Name,
    email: user.Email,
    phoneNumber: user.PhoneNumber,
    organizationName: user.OrganizationName,
    role: user.Role,
    isActive: user.isActive,
    isAdmin: user.isAdmin,
    isArchived: user.isArchived,
    pending: user.Pending,
  };
}
